import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, Download, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import LocationCompanySelector from './Common/LocationCompanySelector.jsx';
import DateRangePicker from '../components/Daterangepicker.jsx'; // adjust path to where your DateRangePicker lives
import { useSuperAdmin } from "../context/SuperAdminContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ---- "Custom Report" tab rules (all companies, all locations) ----
const MIN_START_OFFSET_DAYS = 2; // From date must be at least 2 days before today
const MAX_RANGE_DAYS = 10;       // inclusive of both dates


// ---- Date helpers (local time, "YYYY-MM-DD") ----
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const daysAgo = (n) => {
  const d = startOfToday();
  d.setDate(d.getDate() - n);
  return d;
};
const parseISO = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const toISO = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const prettyDate = (dt) =>
  dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// Returns an error message, or "" when the range is valid
const validateAllCompaniesRange = (startStr, endStr) => {
  if (!startStr || !endStr) return 'Please select a From date and a To date.';

  const start = parseISO(startStr);
  const end = parseISO(endStr);
  const latestAllowed = daysAgo(MIN_START_OFFSET_DAYS); // today - 2 (e.g. 20th when today is 22nd)

  if (start > end) return 'From date cannot be after To date.';
  if (start > latestAllowed || end > latestAllowed) {
    return `Dates must be on or before ${prettyDate(latestAllowed)}.`;
  }

  const span = Math.round((end - start) / 86400000) + 1;
  if (span > MAX_RANGE_DAYS) {
    return `Selected range is ${span} days. Maximum allowed is ${MAX_RANGE_DAYS} days.`;
  }
  return '';
};

const MISReportsPage = () => {
  const navigate = useNavigate();
const { user , token } = useSuperAdmin();
  const hasPayoutAccess = user?.featureAccess?.includes('Payout Accounts');

  // State Management
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '', details: null });
  const [activeTab, setActiveTab] = useState('reports');

  // Location & Company (Generate Reports tab only)
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationObject, setSelectedLocationObject] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  // Custom Report (location/company based) - Generate Reports tab
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Custom Report (ALL companies / locations) - Custom Report tab
  const [allStartDate, setAllStartDate] = useState("");
  const [allEndDate, setAllEndDate] = useState("");

  // Action Titles
  const actionTitles = {
    dailyReport: 'Daily Report',
    weeklyReport: 'Weekly Report',
    customReport: 'Custom Report',
    customReportAll: 'Custom Report',
    payoutFile: 'Payout File',
  };

  // Helper Functions
  const isCustomReportValid = () => {
    if (!customStartDate || !customEndDate) return false;
    const start = new Date(customStartDate);
    const end = new Date(customEndDate);
    return start <= end;
  };

  const allRangeError = validateAllCompaniesRange(allStartDate, allEndDate);
  const isAllRangeValid = () => allRangeError === '';

  const resetForm = () => {
    setSelectedLocation("");
    setSelectedCompany("");
    setSelectedCompanyName("");
    setCustomStartDate("");
    setCustomEndDate("");
    setAllStartDate("");
    setAllEndDate("");
    setNotification({ show: false, type: '', message: '', details: null });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetForm();
  };

  const handleActionClick = (actionKey) => {
    if (actionKey === 'customReportAll') {
      const err = validateAllCompaniesRange(allStartDate, allEndDate);
      if (err) {
        setNotification({ show: true, type: 'error', message: err, details: null });
        return;
      }
    }
    setCurrentAction(actionKey);
    setShowConfirmDialog(true);
  };

  const handleAllDateRangeChange = ({ startDate, endDate }) => {
    setAllStartDate(startDate);
    setAllEndDate(endDate);
    setNotification({ show: false, type: '', message: '', details: null });
  };

  const closeNotification = () => {
    setNotification({ show: false, type: '', message: '', details: null });
  };

  // API Calls
  const generateDailyReport = async () => {
    return await axios.post(
      `${BASE_URL}/on-demand/payment-report/daily-report`,
      { companyId: selectedCompany },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const generateWeeklyReport = async () => {
    return await axios.post(
      `${BASE_URL}/on-demand/payment-report/weekly-report`,
      { reportType: 'weekly', companyId: selectedCompany },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  // Custom report for the selected company / location (Generate Reports tab)
  const generateCustomReport = async () => {
    return await axios.post(
      `${BASE_URL}/on-demand/payment-report/daily-report`,
      {
        companyId: selectedCompany,
        reportType: 'custom',
        startDate: customStartDate,
        endDate: customEndDate
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  // Custom report for ALL companies / locations (Custom Report tab) - no company filter
const generateCustomReportAll = async () => {
  const formatDate = (date) => new Date(date).toISOString().split('T')[0];

  return await axios.get(
    `${BASE_URL}/lenskart/worker-payment-report`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: formatDate(allStartDate),
        endDate: formatDate(allEndDate),
      },
    }
  );
};

  const generatePayoutFile = async () => {
    return await axios.get(`${BASE_URL}/on-demand/payment-report/payout-file`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    setNotification({ show: false, type: '', message: '', details: null });

    try {
      let response;

      switch (currentAction) {
        case 'dailyReport':
          response = await generateDailyReport();
          break;
        case 'weeklyReport':
          response = await generateWeeklyReport();
          break;
        case 'customReport':
          response = await generateCustomReport();
          break;
        case 'customReportAll':
          response = await generateCustomReportAll();
          break;
        case 'payoutFile':
          response = await generatePayoutFile();
          break;
        default:
          throw new Error('Invalid action');
      }

      if (response.data.success) {
        setNotification({
          show: true,
          type: 'success',
          message: response.data.message || 'Report generated successfully!',
          details: response.data.data
        });
      } else {
        setNotification({
          show: true,
          type: 'error',
          message: response.data.message || 'Failed to generate report',
          details: null
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setNotification({
        show: true,
        type: 'error',
        message: error.response?.data?.message || 'An error occurred while generating the report',
        details: error.response?.data?.error
      });
    } finally {
      setLoading(false);
      setCurrentAction(null);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setCurrentAction(null);
  };

  // Render Components
  const renderNotification = () => {
    if (!notification.show) return null;

    return (
      <div className={`mb-6 p-4 rounded-lg border ${
        notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {notification.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
          </div>
          <div className="ml-3 flex-1">
            <p className={`text-sm font-medium ${
              notification.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {notification.message}
            </p>
            {notification.details && (
              <div className="mt-2 text-sm text-gray-700">
                <p>Total Companies: {notification.details.totalCompanies || 0}</p>
                <p>Successful: {notification.details.successful || 0}</p>
                <p>Failed: {notification.details.failed || 0}</p>
                {notification.details.results?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {notification.details.results.map((result, idx) => (
                      <div key={idx} className="border-t pt-2">
                        <p className="font-medium">Company ID: {result.companyId}</p>
                        {result.summary && (
                          <>
                            <p>Period: {result.weekString}</p>
                            <p>Total Workers: {result.summary.totalWorkers}</p>
                            <p>Total Days Worked: {result.summary.totalDaysWorked}</p>
                            <p>Total Payment: ₹{result.summary.totalPayment}</p>
                            <p>Average Attendance: {result.summary.averageAttendance}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {notification.details.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium">Errors:</p>
                    <ul className="list-disc list-inside">
                      {notification.details.errors.map((err, idx) => (
                        <li key={idx}>Company {err.companyId}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={closeNotification} className="ml-3 flex-shrink-0">
            <XCircle className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>
      </div>
    );
  };

  const renderDailyReportCard = () => (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start space-x-4">
        <div className="bg-blue-500 p-3 rounded-lg">
          <FileText className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Daily Report</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate and send daily attendance report for all companies.
          </p>
          <button
            onClick={() => handleActionClick('dailyReport')}
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && currentAction === 'dailyReport' ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Generating...
              </span>
            ) : 'Send Daily Report Email'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderWeeklyReportCard = () => (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start space-x-4">
        <div className="bg-green-500 p-3 rounded-lg">
          <Calendar className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Weekly Report</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate and send weekly attendance report for all companies.
          </p>
          <button
            onClick={() => handleActionClick('weeklyReport')}
            disabled={loading}
            className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && currentAction === 'weeklyReport' ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Generating...
              </span>
            ) : 'Send Weekly Report Email'}
          </button>
        </div>
      </div>
    </div>
  );

  // Custom report for the selected location & company (Generate Reports tab) - unchanged
  const renderCustomReportCard = () => (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow max-w-2xl">
      <div className="flex items-start space-x-4">
        <div className="bg-purple-500 p-3 rounded-lg">
          <Calendar className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Custom Report</h3>
          <p className="text-sm text-gray-600 mb-3">
            Generate and send a custom report for a specific date range for all companies.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <button
            onClick={() => handleActionClick('customReport')}
            disabled={loading || !isCustomReportValid()}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && currentAction === 'customReport' ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Generating...
              </span>
            ) : 'Send Custom Report Email'}
          </button>

          {(!customStartDate || !customEndDate) && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Please select start and end dates
            </p>
          )}
          {customStartDate && customEndDate && !isCustomReportValid() && (
            <p className="text-xs text-red-500 mt-2 text-center">
              End date must be after start date
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Custom report for ALL companies / locations (Custom Report tab) - calendar, no filters
  const renderAllCompaniesCustomCard = () => {
    const hasBothDates = allStartDate && allEndDate;
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow max-w-2xl w-full">
        <div className="flex items-start space-x-4">
          <div className="bg-purple-500 p-3 rounded-lg">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Custom Report</h3>
            <p className="text-sm text-gray-600 mb-4">
              Generate and send custom report for specific date range.
            </p>

            <label className="block text-xs font-medium text-gray-700 mb-1">Date range</label>
            <div className="mb-3">
              <DateRangePicker
                startDate={allStartDate}
                endDate={allEndDate}
                onChange={handleAllDateRangeChange}
                maxDate={toISO(daysAgo(MIN_START_OFFSET_DAYS))}
                maxRangeDays={MAX_RANGE_DAYS}
                placeholder="Select start and end date"
                height="42px"
                disabled={loading}
              />
            </div>

            {hasBothDates && allRangeError && (
              <div className="mb-3 flex items-start text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>{allRangeError}</span>
              </div>
            )}

            <button
              onClick={() => handleActionClick('customReportAll')}
              disabled={loading || !isAllRangeValid()}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && currentAction === 'customReportAll' ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Generating...
                </span>
              ) : 'Send Custom Report Email'}
            </button>

            {!hasBothDates && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Please select  start and end date
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPayoutCard = () => (
    <div className="max-w-md">
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
        <div className="flex items-start space-x-4">
          <div className="bg-orange-500 p-3 rounded-lg">
            <Download className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Payout File</h3>
            <p className="text-sm text-gray-600 mb-1">
              Generate payout file for processing.
            </p>
            <button
              onClick={() => handleActionClick('payoutFile')}
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && currentAction === 'payoutFile' ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Generating...
                </span>
              ) : 'Generate and Send Payout File'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderConfirmationDialog = () => {
    if (!showConfirmDialog) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-yellow-500 mr-3" />
            <h3 className="text-lg font-semibold text-gray-800">Confirm Action</h3>
          </div>
          <p className="text-gray-600 mb-6">
            {currentAction === 'payoutFile' ? (
              <>
                Are you sure you want to generate and send the{' '}
                <strong>Payout File</strong> for <strong>all companies</strong>?
              </>
            ) : currentAction === 'customReportAll' ? (
              <>
                Are you sure you want to generate the <strong>Custom Report</strong> for{' '}
                <strong>all companies and locations</strong> from{' '}
                <strong>{allStartDate}</strong> to <strong>{allEndDate}</strong>?
              
              </>
            ) : currentAction === 'customReport' ? (
              <>
                Are you sure you want to generate and send the{' '}
                <strong>Custom Report</strong> for{' '}
                <strong>{selectedCompanyName} ({selectedLocation})</strong> from{' '}
                <strong>{customStartDate}</strong> to <strong>{customEndDate}</strong>?
              </>
            ) : (
              <>
                Are you sure you want to generate and send the{' '}
                <strong>{actionTitles[currentAction]}</strong> for{' '}
                <strong>{selectedCompanyName} ({selectedLocation})</strong>?
              </>
            )}
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-6">

          {/* Header */}
          <h2 className="text-3xl font-bold mb-2 text-center text-gray-800">Payments Reports</h2>
          <p className="text-center text-gray-600 mb-6">Generate and send payment reports and payout file</p>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => handleTabChange('reports')}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Generate Reports
            </button>
          {(hasPayoutAccess  || user.role=="super_admin") && (
  <button
    onClick={() => handleTabChange('custom')}
    className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
      activeTab === 'custom'
        ? 'border-purple-500 text-purple-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    Custom Report
  </button>
)}
            {/* <button
              onClick={() => handleTabChange('payout')}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'payout'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payout File
            </button> */}
          </div>

          {/* Notification */}
          {renderNotification()}

          {/* Reports Tab (location & company based) */}
          {activeTab === 'reports' && (
            <div>
              <LocationCompanySelector
                selectedLocation={selectedLocation}
                SelectedLocationobject={selectedLocationObject}
                setSelectedLocationobject={setSelectedLocationObject}
                setSelectedLocation={setSelectedLocation}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                setSelectedCompanyname={setSelectedCompanyName}
              />

              {selectedCompany ? (
                <div className="mt-6 space-y-6">
                  {/* First Row: Daily and Weekly Reports */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderDailyReportCard()}
                    {renderWeeklyReportCard()}
                  </div>

                  {/* Second Row: Custom Report (selected company) */}
                  <div className="flex justify-center">
                    {renderCustomReportCard()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 text-gray-400 text-sm">
                  Please select a location and company to view report options.
                </div>
              )}
            </div>
          )}

          {/* Custom Report Tab (all companies & locations, no filter) */}
          {activeTab === 'custom' && (
            <div className="flex justify-center">
              {renderAllCompaniesCustomCard()}
            </div>
          )}

          {/* Payout Tab */}
          {activeTab === 'payout' && renderPayoutCard()}

        </div>
      </div>

      {/* Confirmation Dialog */}
      {renderConfirmationDialog()}

    </div>
  );
};

export default MISReportsPage;